// Pure ledger logic behind the Canton theory widgets. Kept free of React so the
// rules the widgets teach (projection, authorization, consumption, atomicity,
// sequencing) are unit-tested rather than trusted to the UI code.

export type CantonParty = 'Alice' | 'Bob' | 'Bank' | 'Registry' | 'Carol';

export const CANTON_PARTIES: CantonParty[] = ['Alice', 'Bob', 'Bank', 'Registry', 'Carol'];

// ---------------------------------------------------------------------------
// Sub-transaction privacy: a party sees every action it is an informee of,
// together with that action's whole subtree of consequences, and nothing else.
// ---------------------------------------------------------------------------

export interface TxNode {
    id: string;
    action: string;
    detail: string;
    informees: CantonParty[];
    children: TxNode[];
}

// Alice pays Bob $100 of Bank-issued cash for 10 Registry-issued ACME shares.
export const dvpTransaction: TxNode = {
    id: 'settle',
    action: 'Exercise Settle',
    detail: 'on the DvP agreement between Alice and Bob',
    informees: ['Alice', 'Bob'],
    children: [
        {
            id: 'cash-transfer',
            action: 'Exercise Transfer',
            detail: 'on Alice\'s $100 cash Iou issued by Bank',
            informees: ['Bank', 'Alice'],
            children: [
                {
                    id: 'cash-create',
                    action: 'Create Iou',
                    detail: '$100 issued by Bank, now owned by Bob',
                    informees: ['Bank', 'Bob'],
                    children: [],
                },
            ],
        },
        {
            id: 'share-transfer',
            action: 'Exercise Transfer',
            detail: 'on Bob\'s 10 ACME shares recorded by Registry',
            informees: ['Registry', 'Bob'],
            children: [
                {
                    id: 'share-create',
                    action: 'Create Share',
                    detail: '10 ACME recorded by Registry, now held by Alice',
                    informees: ['Registry', 'Alice'],
                    children: [],
                },
            ],
        },
    ],
};

const collectIds = (node: TxNode, into: Set<string>) => {
    into.add(node.id);
    node.children.forEach((child) => collectIds(child, into));
};

export const countNodes = (node: TxNode): number =>
    1 + node.children.reduce((sum, child) => sum + countNodes(child), 0);

/** The ids of the actions in `party`'s projection of the transaction. */
export const projectionFor = (node: TxNode, party: CantonParty): Set<string> => {
    const visible = new Set<string>();
    const walk = (current: TxNode) => {
        if (current.informees.includes(party)) {
            collectIds(current, visible);
            return;
        }
        current.children.forEach(walk);
    };
    walk(node);
    return visible;
};

// ---------------------------------------------------------------------------
// Contract creation: every signatory must authorize; observers never have to.
// ---------------------------------------------------------------------------

export interface ContractShape {
    template: string;
    summary: string;
    signatories: CantonParty[];
    observers: CantonParty[];
}

export const creationShapes: ContractShape[] = [
    {
        template: 'Iou',
        summary: 'Bank promises to pay Alice $100.',
        signatories: ['Bank'],
        observers: ['Alice'],
    },
    {
        template: 'Loan',
        summary: 'Bank lends Alice $1,000; Alice owes repayment.',
        signatories: ['Bank', 'Alice'],
        observers: [],
    },
    {
        template: 'TradeConfirmation',
        summary: 'Alice and Bob confirm a trade; Registry is kept informed.',
        signatories: ['Alice', 'Bob'],
        observers: ['Registry'],
    },
];

export const missingSignatories = (shape: ContractShape, authorizers: CantonParty[]): CantonParty[] =>
    shape.signatories.filter((party) => !authorizers.includes(party));

// ---------------------------------------------------------------------------
// Choices: a tiny Iou ledger where every choice consumes its input contract.
// ---------------------------------------------------------------------------

export interface IouContract {
    id: number;
    issuer: CantonParty;
    owner: CantonParty;
    amount: number;
    active: boolean;
}

export type IouChoice = 'Transfer' | 'Split' | 'Archive';

export interface IouLedger {
    contracts: IouContract[];
    nextId: number;
}

export const initialIouLedger = (): IouLedger => ({
    contracts: [{ id: 1, issuer: 'Bank', owner: 'Alice', amount: 100, active: true }],
    nextId: 2,
});

/** Who the template names as controller of each choice, for a given contract. */
export const controllersOf = (choice: IouChoice, contract: IouContract): CantonParty[] =>
    // Archive is the implicit choice every template gets; its controllers are the signatories.
    choice === 'Archive' ? [contract.issuer] : [contract.owner];

export type ExerciseResult =
    | { ok: true; ledger: IouLedger; archived: number; created: number[] }
    | { ok: false; reason: 'not-visible' | 'archived' | 'unauthorized'; message: string };

export const exerciseIou = (
    ledger: IouLedger,
    contractId: number,
    choice: IouChoice,
    actor: CantonParty,
    newOwner: CantonParty,
): ExerciseResult => {
    const contract = ledger.contracts.find((item) => item.id === contractId);
    if (!contract || (actor !== contract.issuer && actor !== contract.owner)) {
        return {
            ok: false,
            reason: 'not-visible',
            message: `${actor} is not a stakeholder of #${contractId}, so ${actor}'s participant never received it and can't even reference it (contract not found).`,
        };
    }
    if (!contract.active) {
        return {
            ok: false,
            reason: 'archived',
            message: `#${contractId} is archived. It was consumed by an earlier choice, and a consumed contract can never be exercised again.`,
        };
    }
    const controllers = controllersOf(choice, contract);
    if (!controllers.includes(actor)) {
        return {
            ok: false,
            reason: 'unauthorized',
            message: `${choice} on #${contractId} is controlled by ${controllers.join(', ')}; ${actor} submitted it, so the transaction is rejected for missing authorization.`,
        };
    }

    let created: IouContract[] = [];
    let nextId = ledger.nextId;
    if (choice === 'Transfer') {
        created = [{ ...contract, id: nextId++, owner: newOwner, active: true }];
    } else if (choice === 'Split') {
        const first = Math.floor(contract.amount / 2);
        created = [
            { ...contract, id: nextId++, amount: first, active: true },
            { ...contract, id: nextId++, amount: contract.amount - first, active: true },
        ];
    }

    return {
        ok: true,
        archived: contract.id,
        created: created.map((item) => item.id),
        ledger: {
            contracts: [
                ...ledger.contracts.map((item) => (item.id === contract.id ? { ...item, active: false } : item)),
                ...created,
            ],
            nextId,
        },
    };
};

// ---------------------------------------------------------------------------
// Atomic delivery-versus-payment: both legs commit together, or neither does.
// ---------------------------------------------------------------------------

export interface DvpConditions {
    cashActive: boolean;
    sharesActive: boolean;
    bankConfirms: boolean;
    registryConfirms: boolean;
}

export type LegOutcome = 'committed' | 'failed' | 'rolled-back';

export interface Holdings {
    cash: number;
    shares: number;
}

export interface DvpOutcome {
    cashLeg: LegOutcome;
    shareLeg: LegOutcome;
    alice: Holdings;
    bob: Holdings;
    failures: string[];
    /** One leg settled and the other didn't: someone gave without receiving. */
    stranded: boolean;
}

export const settleDvp = (conditions: DvpConditions, atomic: boolean): DvpOutcome => {
    const failures: string[] = [];
    if (!conditions.cashActive) failures.push('Alice\'s cash Iou was already spent');
    if (!conditions.bankConfirms) failures.push('Bank\'s participant rejected the cash leg');
    if (!conditions.sharesActive) failures.push('Bob\'s shares were already transferred');
    if (!conditions.registryConfirms) failures.push('Registry\'s participant rejected the share leg');

    const cashOk = conditions.cashActive && conditions.bankConfirms;
    const sharesOk = conditions.sharesActive && conditions.registryConfirms;

    let cashLeg: LegOutcome;
    let shareLeg: LegOutcome;
    if (atomic) {
        const all = cashOk && sharesOk;
        cashLeg = all ? 'committed' : cashOk ? 'rolled-back' : 'failed';
        shareLeg = all ? 'committed' : sharesOk ? 'rolled-back' : 'failed';
    } else {
        cashLeg = cashOk ? 'committed' : 'failed';
        shareLeg = sharesOk ? 'committed' : 'failed';
    }

    const cashMoved = cashLeg === 'committed';
    const sharesMoved = shareLeg === 'committed';
    return {
        cashLeg,
        shareLeg,
        alice: { cash: cashMoved ? 0 : 100, shares: sharesMoved ? 10 : 0 },
        bob: { cash: cashMoved ? 100 : 0, shares: sharesMoved ? 0 : 10 },
        failures,
        stranded: cashMoved !== sharesMoved,
    };
};

// ---------------------------------------------------------------------------
// Sequencing: two requests spend the same contract; the sequencer's order,
// not anyone reading the contents, decides which one wins.
// ---------------------------------------------------------------------------

export interface SequencedRequest {
    id: string;
    recipient: CantonParty;
}

export interface RaceResult {
    request: SequencedRequest;
    timestamp: string;
    committed: boolean;
    note: string;
}

export const resolveRace = (ordered: SequencedRequest[], contractId: number): RaceResult[] => {
    let spentBy: string | null = null;
    return ordered.map((request, index) => {
        const timestamp = `12:00:00.${String(index + 1).padStart(3, '0')}`;
        if (spentBy === null) {
            spentBy = request.id;
            return { request, timestamp, committed: true, note: `#${contractId} is active: validated, confirmed, committed. #${contractId} is now archived.` };
        }
        return { request, timestamp, committed: false, note: `#${contractId} was already consumed by ${spentBy}, sequenced earlier. The activeness check fails and the request is rejected.` };
    });
};
